import { ReportsToEditor } from "@/components/profile/ReportsToEditor";
import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { toast } from "sonner";
import {
  Edit2,
  LogOut,
  Phone,
  MapPin,
  Plus,
  Trash2,
  User,
  PlaneTakeoff,
  Briefcase,
  Building2,
  Calendar,
  Hash,
  Clock,
  Globe,
  CreditCard,
  Landmark,
  FileText,
  StickyNote,
  GraduationCap,
  Monitor,
  Stamp,
  ChevronRight,
  Settings,
  Baby,
} from "lucide-react";
import MobileLayout from "./MobileLayout";
import { getContractTypeLabel } from "@/lib/employment-contract-types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { ProfileAboutTab } from "@/components/profile/ProfileAboutTab";
import { AnnualLeaveBalanceSummary } from "@/components/leave/AnnualLeaveBalanceSummary";
import { LeaveRequestDialog } from "@/components/leave/LeaveRequestDialog";
import { useAuth } from "@/contexts/AuthContext";
import { calcTenure, formatStartDate } from "@/data/employees";
import {
  formatLeaveDateRange,
  getLeaveStatusLabel,
  getLeaveTypeLabel,
} from "@/lib/leave-i18n";
import {
  apiGetEmployeeByEmail,
  apiGetEmployees,
  apiUpdateEmployee,
  apiGetEmergencyContacts,
  apiCreateEmergencyContact,
  apiDeleteEmergencyContact,
  apiGetChildren,
  apiCreateChild,
  apiDeleteChild,
  apiGetMyLeaveBalance,
  apiGetMyLeaveRequests,
  apiGetLeaveCompanyPolicy,
  apiGetBankDetails,
  apiUpdateBankDetails,
  apiGetSalary,
  apiGetNotes,
  apiGetCompanyCurrency,
  apiGetQualifications,
  apiGetEquipment,
  apiGetVisa,
  type EmergencyContact,
  type EmployeeChild,
  type LeaveBalance,
  type LeaveCompanyPolicy,
  type BankDetails,
  type Qualification,
  type Equipment,
  type Visa,
} from "@/lib/api";

// ── Edit field sheet ──────────────────────────────────────────────────────────
function EditFieldSheet({
  open,
  onOpenChange,
  label,
  value,
  onSave,
  type = "text",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  label: string;
  value: string;
  onSave: (val: string) => Promise<void> | void;
  type?: string;
}) {
  const { t } = useTranslation();
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(val);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setVal(value);
        onOpenChange(v);
      }}
    >
      <DialogContent className="w-[90vw] max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("mobile.profile.edit")} {label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{label}</Label>
            <Input
              type={type}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              autoFocus
            />
          </div>
          <Button
            className="w-full gradient-brand text-primary-foreground"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? t("mobile.profile.saving") : t("mobile.profile.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Read-only info row ────────────────────────────────────────────────────────
function InfoRow({
  icon: Icon,
  label,
  value,
  last = false,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
  last?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${!last ? "border-b border-border/30" : ""}`}>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground truncate">
          {value || <span className="italic text-muted-foreground text-xs">{t("mobile.profile.notSet")}</span>}
        </p>
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title }: { title: string }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
      {title}
    </p>
  );
}

// ── Personal Info tab ─────────────────────────────────────────────────────────
function MobilePersonalInfoTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const qc = useQueryClient();
  const { user, updateUser } = useAuth();

  const [editField, setEditField] = useState<null | {
    label: string;
    field: string;
    value: string;
    type?: string;
    isBankField?: boolean;
  }>(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({
    contactName: "",
    relationship: "",
    phone: "",
    email: "",
  });
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChild, setNewChild] = useState({ firstName: "", dateOfBirth: "" });

  const { data: employee } = useQuery({
    queryKey: ["mobile-employee-profile", user?.email],
    queryFn: () => apiGetEmployeeByEmail(user!.email),
    enabled: !!user?.email,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ["mobile-emergency-contacts", employeeId],
    queryFn: () => apiGetEmergencyContacts(employeeId),
    staleTime: 2 * 60_000,
  });

  const { data: children = [] } = useQuery({
    queryKey: ["mobile-children", employeeId],
    queryFn: () => apiGetChildren(employeeId),
    staleTime: 2 * 60_000,
  });

  const { data: salary } = useQuery({
    queryKey: ["mobile-salary", employeeId],
    queryFn: () => apiGetSalary(employeeId),
    staleTime: 5 * 60_000,
  });

  const { data: currencyData } = useQuery({
    queryKey: ["company-currency"],
    queryFn: apiGetCompanyCurrency,
    staleTime: 60 * 60_000,
  });
  const companyCurrency = currencyData?.currency ?? "—";

  const { data: bankDetails } = useQuery({
    queryKey: ["mobile-bank-details", employeeId],
    queryFn: () => apiGetBankDetails(employeeId),
    staleTime: 5 * 60_000,
  });

  const { data: notes = [] } = useQuery({
    queryKey: ["mobile-notes", employeeId],
    queryFn: () => apiGetNotes(employeeId),
    staleTime: 5 * 60_000,
  });

  const { data: qualifications = [] } = useQuery({
    queryKey: ["mobile-qualifications", employeeId],
    queryFn: () => apiGetQualifications(employeeId),
    staleTime: 5 * 60_000,
  });

  const { data: equipment = [] } = useQuery({
    queryKey: ["mobile-equipment", employeeId],
    queryFn: () => apiGetEquipment(employeeId),
    staleTime: 5 * 60_000,
  });

  const { data: visa } = useQuery({
    queryKey: ["mobile-visa", employeeId],
    queryFn: () => apiGetVisa(employeeId),
    staleTime: 5 * 60_000,
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: (data: Record<string, string>) => apiUpdateEmployee(employeeId, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["mobile-employee-profile"] });
      if (updated.name !== user?.name) {
        updateUser({
          name: updated.name,
          initials: updated.name.split(" ").map((p: string) => p[0]).join("").toUpperCase().slice(0, 2),
        });
      }
      toast.success(t("mobile.profile.saved"));
    },
    onError: () => toast.error(t("mobile.profile.saveFailed")),
  });

  const updateBankMutation = useMutation({
    mutationFn: (data: Partial<Omit<BankDetails, "id" | "employeeId">>) =>
      apiUpdateBankDetails(employeeId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-bank-details", employeeId] });
      toast.success(t("mobile.profile.saved"));
    },
    onError: () => toast.error(t("mobile.profile.saveFailed")),
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: number) => apiDeleteEmergencyContact(employeeId, contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mobile-emergency-contacts", employeeId] }),
    onError: () => toast.error(t("mobile.profile.contactDeleteFailed")),
  });

  const addContactMutation = useMutation({
    mutationFn: () =>
      apiCreateEmergencyContact(employeeId, {
        ...newContact,
        relationship: newContact.relationship || null,
        phone: newContact.phone || null,
        email: newContact.email || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-emergency-contacts", employeeId] });
      setShowAddContact(false);
      setNewContact({ contactName: "", relationship: "", phone: "", email: "" });
      toast.success(t("mobile.profile.contactAdded"));
    },
    onError: () => toast.error(t("mobile.profile.contactAddFailed")),
  });

  const deleteChildMutation = useMutation({
    mutationFn: (childId: number) => apiDeleteChild(employeeId, childId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mobile-children", employeeId] }),
    onError: () => toast.error(t("mobile.profile.saveFailed")),
  });

  const addChildMutation = useMutation({
    mutationFn: () => apiCreateChild(employeeId, { firstName: newChild.firstName, dateOfBirth: newChild.dateOfBirth }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-children", employeeId] });
      setShowAddChild(false);
      setNewChild({ firstName: "", dateOfBirth: "" });
      toast.success(t("mobile.profile.saved"));
    },
    onError: () => toast.error(t("mobile.profile.saveFailed")),
  });

  const openEdit = (label: string, field: string, value: string, type = "text", isBankField = false) =>
    setEditField({ label, field, value, type, isBankField });

  const salaryDisplay = salary?.baseSalary != null
    ? `${companyCurrency} ${salary.baseSalary.toLocaleString()}${salary.payFrequency ? ` / ${salary.payFrequency}` : ""}`
    : null;
  const bonusDisplay = salary?.bonus != null
    ? `${companyCurrency} ${salary.bonus.toLocaleString()}`
    : null;

  return (
    <div className="px-4 py-3 space-y-5 pb-6">

      {/* ── Personal details ─────────────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.personalDetails")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          {([
            { label: t("mobile.profile.fullName"), field: "name", icon: User, value: employee?.name, type: "text" },
            { label: t("mobile.profile.phone"), field: "phone", icon: Phone, value: employee?.phone, type: "tel" },
            { label: t("mobile.profile.address"), field: "address", icon: MapPin, value: employee?.address, type: "text" },
            { label: t("mobile.profile.nationality"), field: "nationality", icon: Globe, value: employee?.nationality, type: "text" },
            { label: t("mobile.profile.gender"), field: "gender", icon: User, value: employee?.gender, type: "text" },
          ] as const).map(({ label, field, icon: Icon, value, type }, i, arr) => (
            <button
              key={field}
              type="button"
              onClick={() => openEdit(label, field, value ?? "", type)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left ${i < arr.length - 1 ? "border-b border-border/30" : ""}`}
            >
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground">{label}</p>
                <p className="text-sm font-medium text-foreground truncate">
                  {value || <span className="italic text-muted-foreground text-xs">{t("mobile.profile.notSet")}</span>}
                </p>
              </div>
              <Edit2 className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* ── HR Info (read-only) ──────────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.hrInfo")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          <InfoRow icon={Hash} label={t("mobile.profile.employeeId")} value={employee?.employeeId} />
          <InfoRow icon={Briefcase} label={t("mobile.profile.jobTitle")} value={employee?.jobTitle} />
          <InfoRow icon={Building2} label={t("mobile.profile.department")} value={employee?.department} />
          <InfoRow icon={FileText} label={t("mobile.profile.contractType")} value={getContractTypeLabel(employee?.contractType, t)} />
          <InfoRow icon={Clock} label={t("mobile.profile.workSchedule")} value={employee?.workSchedule} />
          <InfoRow icon={User} label={t("mobile.profile.status")} value={employee?.status} last />
        </div>
      </div>

      {/* ── Birthday & Hire Date (read-only) ────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.birthdayHire")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          <InfoRow icon={Calendar} label={t("mobile.profile.birthday")} value={employee?.birthday} />
          <InfoRow
            icon={Calendar}
            label={t("mobile.profile.hireDate")}
            value={employee?.startDate ? `${formatStartDate(employee.startDate)} (${calcTenure(employee.startDate)})` : null}
            last
          />
        </div>
      </div>

      {/* ── Salary (read-only) ───────────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.salary")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          <InfoRow icon={Landmark} label={t("mobile.profile.baseSalary")} value={salaryDisplay} />
          <InfoRow icon={Landmark} label={t("mobile.profile.bonus")} value={bonusDisplay} />
          <Link
            to="/mobile/salary"
            className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40 active:bg-muted/60 transition-colors border-t border-border/30"
          >
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-muted-foreground">{t("mobile.profile.payslips")}</p>
              <p className="text-sm font-medium text-foreground">{t("mobile.profile.viewSalarySlips")}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
          </Link>
        </div>
      </div>

      {/* ── Bank Details (editable) ──────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.bankDetails")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          {([
            { label: t("mobile.profile.accountHolder"), field: "accountHolderName", icon: CreditCard, value: bankDetails?.accountHolderName },
            { label: t("mobile.profile.bankName"), field: "bankName", icon: Landmark, value: bankDetails?.bankName },
            { label: t("mobile.profile.iban"), field: "iban", icon: CreditCard, value: bankDetails?.iban },
            { label: t("mobile.profile.bicSwift"), field: "bicSwift", icon: CreditCard, value: bankDetails?.bicSwift },
          ] as const).map(({ label, field, icon: Icon, value }, i, arr) => (
            <button
              key={field}
              type="button"
              onClick={() => openEdit(label, field, value ?? "", "text", true)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left ${i < arr.length - 1 ? "border-b border-border/30" : ""}`}
            >
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground">{label}</p>
                <p className="text-sm font-medium text-foreground truncate">
                  {value || <span className="italic text-muted-foreground text-xs">{t("mobile.profile.notSet")}</span>}
                </p>
              </div>
              <Edit2 className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Emergency Contacts ───────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <SectionHeader title={t("mobile.profile.emergencyContacts")} />
          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={() => setShowAddContact(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t("mobile.profile.add")}
          </Button>
        </div>
        {contacts.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">{t("mobile.profile.noContacts")}</p>
        ) : (
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {contacts.map((c, i) => (
              <div key={c.id} className={`flex items-center gap-3 px-4 py-3.5 ${i < contacts.length - 1 ? "border-b border-border/30" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{c.contactName}</p>
                  <p className="text-xs text-muted-foreground">
                    {[c.relationship, c.phone, c.email].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive/60 hover:text-destructive"
                  onClick={() => deleteContactMutation.mutate(c.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Children ────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <SectionHeader title={t("mobile.profile.children")} />
          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={() => setShowAddChild(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t("mobile.profile.add")}
          </Button>
        </div>
        {children.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">{t("mobile.profile.noChildren")}</p>
        ) : (
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {children.map((child, i) => {
              const dob = new Date(child.dateOfBirth);
              const today = new Date();
              let age = today.getFullYear() - dob.getFullYear();
              const m = today.getMonth() - dob.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
              return (
                <div key={child.id} className={`flex items-center gap-3 px-4 py-3.5 ${i < children.length - 1 ? "border-b border-border/30" : ""}`}>
                  <Baby className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{child.firstName}</p>
                    <p className="text-xs text-muted-foreground">{child.dateOfBirth} · {age} {age === 1 ? t("profile.children.yearOld") : t("profile.children.yearsOld")}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive/60 hover:text-destructive"
                    onClick={() => deleteChildMutation.mutate(child.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Qualifications (read-only) ──────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.qualifications")} />
        {qualifications.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">{t("mobile.profile.noQualifications")}</p>
        ) : (
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {qualifications.map((q, i) => (
              <div key={q.id} className={`flex items-start gap-3 px-4 py-3.5 ${i < qualifications.length - 1 ? "border-b border-border/30" : ""}`}>
                <GraduationCap className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{q.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {[q.type === "education" ? t("mobile.profile.education") : q.type === "training" ? t("mobile.profile.training", "Training") : t("mobile.profile.certification"), q.institution].filter(Boolean).join(" · ")}
                  </p>
                  {(q.dateObtained || q.expiryDate) && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {q.dateObtained ? `${t("mobile.profile.obtained")}: ${q.dateObtained}` : ""}
                      {q.dateObtained && q.expiryDate ? " · " : ""}
                      {q.expiryDate ? `${t("mobile.profile.expires")}: ${q.expiryDate}` : ""}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Equipment (read-only) ────────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.equipment")} />
        {equipment.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">{t("mobile.profile.noEquipment")}</p>
        ) : (
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {equipment.map((e, i) => (
              <div key={e.id} className={`flex items-start gap-3 px-4 py-3.5 ${i < equipment.length - 1 ? "border-b border-border/30" : ""}`}>
                <Monitor className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{e.itemName}</p>
                  <p className="text-xs text-muted-foreground">
                    {[e.serialNumber ? `${t("mobile.profile.serialNumber")}: ${e.serialNumber}` : null, e.status].filter(Boolean).join(" · ")}
                  </p>
                  {e.assignedDate && (
                    <p className="text-xs text-muted-foreground mt-0.5">{t("mobile.profile.assigned")}: {e.assignedDate}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Visa / Work Permit (read-only) ──────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.visa")} />
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          <InfoRow icon={Stamp} label={t("mobile.profile.permitType")} value={visa?.permitType} />
          <InfoRow icon={Stamp} label={t("mobile.profile.permitNumber")} value={visa?.permitNumber} />
          <InfoRow icon={Globe} label={t("mobile.profile.issuingCountry")} value={visa?.issuingCountry} />
          <InfoRow icon={Calendar} label={t("mobile.profile.issueDate")} value={visa?.issueDate} />
          <InfoRow icon={Calendar} label={t("mobile.profile.expiryDate")} value={visa?.expiryDate} last />
        </div>
      </div>

      {/* ── Notes (read-only) ────────────────────────────── */}
      <div className="space-y-2">
        <SectionHeader title={t("mobile.profile.notes")} />
        {notes.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground">
            <StickyNote className="h-6 w-6 opacity-30" />
            <p className="text-xs">{t("mobile.profile.noNotes")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="bg-card border border-border/60 rounded-xl px-4 py-3">
                <p className="text-sm text-foreground leading-relaxed">{n.content}</p>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {n.authorName ?? "HR"} · {format(parseISO(n.createdAt), "MMM d, yyyy", { locale: dateLocale })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Edit field sheet ─────────────────────────────── */}
      {editField && (
        <EditFieldSheet
          open={!!editField}
          onOpenChange={(v) => { if (!v) setEditField(null); }}
          label={editField.label}
          value={editField.value}
          type={editField.type}
          onSave={async (val) => {
            if (editField.isBankField) {
              await updateBankMutation.mutateAsync({ [editField.field]: val });
              return;
            }
            await updateEmployeeMutation.mutateAsync({ [editField.field]: val });
          }}
        />
      )}

      {/* ── Add child sheet ──────────────────────────────── */}
      <Sheet open={showAddChild} onOpenChange={setShowAddChild}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="mb-4">
            <SheetTitle>{t("mobile.profile.addChild")}</SheetTitle>
          </SheetHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("profile.children.firstName")} *</Label>
              <Input
                type="text"
                value={newChild.firstName}
                onChange={(e) => setNewChild((prev) => ({ ...prev, firstName: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("profile.children.dateOfBirth")} *</Label>
              <Input
                type="date"
                value={newChild.dateOfBirth}
                onChange={(e) => setNewChild((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                className="h-11"
              />
            </div>
            <Button
              className="w-full gradient-brand text-primary-foreground mt-2"
              disabled={!newChild.firstName || !newChild.dateOfBirth || addChildMutation.isPending}
              onClick={() => addChildMutation.mutate()}
            >
              {addChildMutation.isPending ? t("mobile.profile.adding") : t("mobile.profile.addChild")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Add contact sheet ────────────────────────────── */}
      <Sheet open={showAddContact} onOpenChange={setShowAddContact}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="mb-4">
            <SheetTitle>{t("mobile.profile.addContact")}</SheetTitle>
          </SheetHeader>
          <div className="space-y-3">
            {([
              { label: `${t("mobile.profile.fullName")} *`, key: "contactName", type: "text" },
              { label: t("mobile.profile.relationship"), key: "relationship", type: "text" },
              { label: t("mobile.profile.phone"), key: "phone", type: "tel" },
              { label: t("mobile.profile.email"), key: "email", type: "email" },
            ] as const).map(({ label, key, type }) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input
                  type={type}
                  value={newContact[key]}
                  onChange={(e) => setNewContact((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="h-11"
                />
              </div>
            ))}
            <Button
              className="w-full gradient-brand text-primary-foreground mt-2"
              disabled={!newContact.contactName || addContactMutation.isPending}
              onClick={() => addContactMutation.mutate()}
            >
              {addContactMutation.isPending ? t("mobile.profile.adding") : t("mobile.profile.addContactBtn")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Time Off tab ──────────────────────────────────────────────────────────────
function MobileTimeOffTab({
  employeeId,
  balances,
  companyPolicy,
  loading, error, onRetry,
}: {
  employeeId: string;
  balances: LeaveBalance[];
  companyPolicy?: LeaveCompanyPolicy;
  loading: boolean; error: boolean; onRetry: () => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const { data: requests = [] } = useQuery({
    queryKey: ["mobile-my-leave-requests", employeeId],
    queryFn: () => apiGetMyLeaveRequests(),
    staleTime: 2 * 60_000,
  });

  const sorted = [...requests].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  const annualBalance = balances.find((balance) => balance.leaveType === "annual");
  const otherBalances = balances.filter(
    (balance) => balance.countsAgainstBalance && balance.leaveType !== "annual",
  );

  const STATUS_STYLES: Record<string, string> = {
    Approved: "bg-emerald-100 text-emerald-700",
    Pending: "bg-amber-100 text-amber-700",
    Rejected: "bg-red-100 text-red-700",
    Cancelled: "bg-slate-100 text-slate-600",
  };

  return (
    <div className="px-4 py-3 space-y-4">
      <Button
        onClick={() => setShowLeaveDialog(true)}
        className="w-full h-11 gradient-brand text-primary-foreground rounded-xl gap-2"
      >
        <PlaneTakeoff className="h-4 w-4" />
        {t("mobile.timeoff.requestLeave")}
      </Button>

      {/* Balances */}
      {(annualBalance || loading || error) && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("mobile.timeoff.leaveBalance")}
          </p>
          <AnnualLeaveBalanceSummary
            balance={annualBalance}
            policy={companyPolicy} loading={loading} error={error} onRetry={onRetry}
            className="border-border/60 shadow-none"
          />
          {otherBalances.length > 0 && (
            <div className="space-y-2">
              {otherBalances.map((b) => (
                <div
                  key={b.leaveType}
                  className="bg-card border border-border/60 rounded-xl px-4 py-3"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-medium text-foreground">
                      {getLeaveTypeLabel(t, b.leaveType, b.leaveTypeLabel)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {b.availableNow} / {(b.totalDays ?? 0) + b.carryOverDays} {t("mobile.timeoff.daysLeft")}
                    </p>
                  </div>
                  <Progress
                    value={
                      (b.totalDays ?? 0) + b.carryOverDays > 0
                        ? ((b.usedDays + b.pendingDays) / ((b.totalDays ?? 0) + b.carryOverDays)) * 100
                        : 0
                    }
                    className="h-1.5"
                  />
                  {b.pendingDays > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {b.pendingDays} {b.pendingDays !== 1 ? t("mobile.timeoff.daysPending") : t("mobile.timeoff.dayPending")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Request history */}
      {sorted.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("mobile.timeoff.history")}
          </p>
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {sorted.slice(0, 10).map((r, i) => (
              <div
                key={r.id}
                className={`flex items-center gap-3 px-4 py-3 ${
                  i < Math.min(sorted.length, 10) - 1 ? "border-b border-border/30" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {getLeaveTypeLabel(t, r.leaveType, r.leaveTypeLabel)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatLeaveDateRange(r.startDate, r.endDate, dateLocale)} ·{" "}
                    {r.workingDays} {r.workingDays !== 1 ? t("mobile.timeoff.days") : t("mobile.timeoff.day")}
                  </p>
                </div>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    STATUS_STYLES[r.status] ?? "bg-muted text-muted-foreground"
                  }`}
                >
                  {getLeaveStatusLabel(t, r.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <LeaveRequestDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        balances={balances}
        currentEmployeeId={employeeId}
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MobileProfile() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { data: managerCandidates = [] } = useQuery({ queryKey: ["mobile-employees"], queryFn: apiGetEmployees, enabled: user?.role.mainRole === "hr" || user?.role.mainRole === "admin" });
  const navigate = useNavigate();
  const handleSignOut = async () => {
    await logout();
    navigate("/mobile/login");
  };

  const { data: employee } = useQuery({
    queryKey: ["mobile-employee-profile", user?.email],
    queryFn: () => apiGetEmployeeByEmail(user!.email),
    enabled: !!user?.email,
    staleTime: 2 * 60_000,
  });

  const { data: balances = [], isPending: balanceLoading, isError: balanceError, refetch: refetchBalance } = useQuery({
    queryKey: ["mobile-leave-balance"],
    queryFn: () => apiGetMyLeaveBalance(),
    enabled: !!employee,
    staleTime: 5 * 60_000,
  });

  const { data: companyPolicy, isPending: policyLoading, isError: policyError, refetch: refetchPolicy } = useQuery({
    queryKey: ["leaveCompanyPolicy"],
    queryFn: apiGetLeaveCompanyPolicy,
    enabled: !!employee,
    staleTime: 5 * 60_000,
  });

  const profileHeader = employee ? (
    <div className="flex items-center gap-3 flex-1">
      <EmployeeAvatar
        avatarUrl={employee.avatarUrl}
        avatarColor={employee.avatarColor}
        initials={employee.initials}
        name={employee.name}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate leading-tight">
          {employee.name}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">{employee.jobTitle}</p>
      </div>
      <Link to="/mobile/settings">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
          title="Settings"
        >
          <Settings className="h-4 w-4" />
        </Button>
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
        onClick={handleSignOut}
        title="Sign out"
      >
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-between flex-1">
      <Link to="/mobile" aria-label="Home">
        <img src="/besthr.png" alt="BestHR" className="h-7 object-contain" />
      </Link>
      <div className="flex items-center gap-1">
        <Link to="/mobile/settings">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
            <Settings className="h-4 w-4" />
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <MobileLayout header={profileHeader} hideOrgLogo>
      <Tabs defaultValue="about">
        <div className="px-4 pt-3 pb-1 sticky top-0 z-10 bg-background/95 backdrop-blur">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="about" className="text-xs">{t("mobile.profile.tabs.about")}</TabsTrigger>
            <TabsTrigger value="info" className="text-xs">{t("mobile.profile.tabs.info")}</TabsTrigger>
            <TabsTrigger value="timeoff" className="text-xs">{t("mobile.profile.tabs.timeoff")}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="about" className="mt-0 px-4 py-2">
          {employee ? (
            <>
              <ReportsToEditor employee={employee} employees={managerCandidates} />
              <ProfileAboutTab employee={employee} isPublicOnly={false} />
            </>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("mobile.profile.loadingProfile")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="info" className="mt-0">
          {employee ? (
            <MobilePersonalInfoTab employeeId={employee.id} />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("mobile.profile.loading")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeoff" className="mt-0">
          {employee ? (
            <MobileTimeOffTab
              employeeId={employee.id}
              balances={balances}
              companyPolicy={companyPolicy}
              loading={balanceLoading || policyLoading} error={balanceError || policyError}
              onRetry={() => { void refetchBalance(); void refetchPolicy(); }}
            />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("mobile.profile.loading")}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </MobileLayout>
  );
}
